import React, { useState, useEffect, useContext } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import MoneyOffRoundedIcon from "@mui/icons-material/MoneyOffRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import MapsHomeWorkRoundedIcon from "@mui/icons-material/MapsHomeWorkRounded";

import KpiCard from "../../components/ui/KpiCard";
import KpiMultiCard from "../../components/ui/KpiMultiCard";
import { DataContext } from "../../context/DataContext";
import { getToken } from "../../api/Account";
import { getRuta, isAccess } from "../../helpers/Helpers";
import {
  getBarriosConCarteraAsync,
  getActiveClientsAsync,
  getActiveCreditsAsync,
  getVecidoCreditsAsync,
  getCreditsPendienteAsync,
  getTotalCobradoAsync,
  getMoraTotalAsync,
  getTasaMorosidadAsync,
  getCreditosPorVencerAsync,
  getColocacionMensualComparativoAsync,
  getNuevosClientesAsync,
  getGastosDelMesAsync,
} from "../../api/Dashboard";
import { getListTipoCambioAsync } from "../../api/TipoCambio";

// Encabezado de grupo, tomado del dashboard de la rama Inver-Credit_Kevin.
const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: 12.5,
      fontWeight: 600,
      color: "text.secondary",
      letterSpacing: ".2px",
      marginBottom: "10px",
    }}
  >
    {children}
  </Typography>
);

// `auto-fit` colapsa las columnas que sobran y reparte el ancho entre las
// tarjetas que hay, asi cada seccion llena su fila completa. Se probo con
// columnas fijas (repeat(4,1fr)) buscando que todas midieran igual, pero las
// secciones de 3 tarjetas dejaban una celda vacia a la derecha: preferible que
// el ancho varie un poco entre grupos a que queden huecos.
const CardGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(auto-fit, minmax(220px, 1fr))",
      },
      gap: "16px",
    }}
  >
    {children}
  </Box>
);

// null significa "el indicador no se pudo cargar": se muestra un guion en vez
// de un cero, que se leeria como un dato real.
const SIN_DATO = "—";

const cordobas = (n) =>
  n === null || n === undefined
    ? SIN_DATO
    : new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }).format(n);

const dolares = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const numero = (n) => (n === null || n === undefined ? SIN_DATO : n);

/**
 * Grilla de indicadores — ver design_handoff_crediapp_redesign/Dashboard CrediApp.dc.html:
 * grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px.
 *
 * Las seis consultas se lanzan juntas desde aca en vez de una por tarjeta. Antes
 * cada componente hacia su propio fetch con su propio setIsLoading, asi que el
 * overlay de carga se apagaba cuando terminaba LA PRIMERA, no la ultima. Ademas,
 * tener los seis valores en un mismo lugar permite derivar contexto entre ellos
 * (los subtitulos de abajo) sin pedir nada extra al backend.
 */
const HomeInfo = ({ selectedSucursal }) => {
  const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
  const token = getToken();
  const ruta = getRuta();
  const navigate = useNavigate();

  const [datos, setDatos] = useState(null);
  const [tasaDolar, setTasaDolar] = useState(0);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const [
        clientes,
        activos,
        vencidos,
        pendientes,
        cobrado,
        mora,
        morosidad,
        porVencer,
        colocacion,
        nuevosClientes,
        gastos,
        barrios,
        tipoCambio,
      ] = await Promise.all([
        getActiveClientsAsync(token, selectedSucursal),
        getActiveCreditsAsync(token, selectedSucursal),
        getVecidoCreditsAsync(token, selectedSucursal),
        getCreditsPendienteAsync(token, selectedSucursal),
        getTotalCobradoAsync(token, selectedSucursal),
        getMoraTotalAsync(token, selectedSucursal),
        getTasaMorosidadAsync(token, selectedSucursal),
        getCreditosPorVencerAsync(token, selectedSucursal),
        getColocacionMensualComparativoAsync(token, selectedSucursal),
        getNuevosClientesAsync(token, selectedSucursal),
        getGastosDelMesAsync(token, selectedSucursal),
        getBarriosConCarteraAsync(token, selectedSucursal),
        getListTipoCambioAsync(token),
      ]);

      setIsLoading(false);

      const respuestas = [
        clientes,
        activos,
        vencidos,
        pendientes,
        cobrado,
        mora,
        morosidad,
        porVencer,
        colocacion,
        nuevosClientes,
        gastos,
        barrios,
      ];

      // La sesion caida si es fatal: no tiene sentido pintar el panel.
      if (respuestas.some((r) => r.error === "Unauthorized")) {
        setIsUnautorized(true);
        return;
      }

      // Cualquier otro fallo degrada SOLO ese indicador. Antes un unico endpoint
      // caido cortaba el flujo y dejaba el dashboard completamente en blanco.
      // Devuelve null (no 0) para que la tarjeta muestre "—" y no un cero que
      // se leeria como dato real.
      const valor = (r, porDefecto = null) =>
        r.statusResponse && r.data.result !== undefined && r.data.result !== null
          ? r.data.result
          : porDefecto;

      setDatos({
        clientes: valor(clientes),
        activos: valor(activos),
        vencidos: valor(vencidos),
        pendientes: valor(pendientes),
        cobrado: valor(cobrado),
        mora: valor(mora),
        morosidad: valor(morosidad),
        porVencer: valor(porVencer),
        colocacion: valor(colocacion, { mesActual: 0, mesAnterior: 0 }),
        nuevosClientes: valor(nuevosClientes, { hoy: 0, semana: 0, mes: 0 }),
        gastos: valor(gastos),
        barrios: valor(barrios),
      });

      // La tasa es opcional: si falla, los KPI monetarios simplemente no
      // muestran el equivalente en dolares.
      if (tipoCambio.statusResponse) {
        const lista = tipoCambio.data.result || [];
        const reciente = lista.reduce(
          (mayor, item) =>
            !mayor || new Date(item.fecha) > new Date(mayor.fecha) ? item : mayor,
          null
        );
        setTasaDolar(reciente ? reciente.dolar : 0);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSucursal]);

  if (!datos) {
    return null;
  }

  // Contexto derivado de los mismos valores ya cargados: no cuesta llamadas.
  const enDolares = (monto) =>
    tasaDolar > 0 && monto !== null && monto !== undefined ? dolares(monto / tasaDolar) : undefined;

  const porcentajeVencidos =
    datos.activos > 0 ? Math.round((datos.vencidos / datos.activos) * 100) : 0;

  const creditosPorCliente =
    datos.clientes > 0 ? (datos.activos / datos.clientes).toFixed(1) : "0";

  const creditosPorBarrio =
    datos.barrios > 0 ? (datos.activos / datos.barrios).toFixed(1) : "0";

  const puedeVerBarrios = isAccess(access, "RCREDBARRIO");

  // Cuanto de lo entregado esta aun pendiente de desembolsar.
  const totalCreditos = datos.activos + datos.pendientes;
  const porcentajePendientes =
    totalCreditos > 0 ? Math.round((datos.pendientes / totalCreditos) * 100) : 0;

  // Variacion de colocacion contra el mes anterior. Es el unico KPI con
  // comparativo real, porque es el unico endpoint que devuelve ambos periodos.
  const { mesActual = 0, mesAnterior = 0 } = datos.colocacion || {};
  const variacion =
    mesAnterior > 0 ? ((mesActual - mesAnterior) / mesAnterior) * 100 : null;

  const deltaColocacion =
    variacion === null
      ? undefined
      : `${variacion >= 0 ? "↑" : "↓"} ${Math.abs(variacion).toFixed(1)}%`;

  const tonoColocacion = variacion === null ? "flat" : variacion >= 0 ? "up" : "down";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <Box>
        <SectionLabel>Cartera</SectionLabel>
        <CardGrid>
          <KpiCard
            icon={<GroupRoundedIcon fontSize="inherit" />}
            iconColor="success.main"
            iconBg="success.light"
            value={numero(datos.clientes)}
            label="Clientes activos"
            sub={`${creditosPorCliente} créditos por cliente`}
            onClick={() => navigate(`${ruta}/clients`)}
          />

          <KpiCard
            icon={<CreditCardRoundedIcon fontSize="inherit" />}
            iconColor="success.main"
            iconBg="success.light"
            value={numero(datos.activos)}
            label="Créditos activos"
            sub={`${datos.pendientes} pendientes de entrega`}
            onClick={() => navigate(`${ruta}/credits`)}
          />

          <KpiCard
            icon={<MoneyOffRoundedIcon fontSize="inherit" />}
            iconColor="error.main"
            iconBg="error.light"
            value={numero(datos.vencidos)}
            label="Créditos vencidos"
            sub={`${porcentajeVencidos}% de la cartera activa`}
            onClick={() => navigate(`${ruta}/credits`)}
          />

          <KpiCard
            icon={<ScheduleRoundedIcon fontSize="inherit" />}
            iconColor="success.main"
            iconBg="success.light"
            value={numero(datos.pendientes)}
            label="Pend. de entrega"
            sub={`${porcentajePendientes}% del total de créditos`}
            onClick={() => navigate(`${ruta}/credits`)}
          />

          <KpiCard
            icon={<MapsHomeWorkRoundedIcon fontSize="inherit" />}
            iconColor="success.main"
            iconBg="success.light"
            value={numero(datos.barrios)}
            label="Barrios con cartera"
            sub={`${creditosPorBarrio} créditos por barrio`}
            // Solo navega si el rol puede ver el reporte: llevarlo a una pantalla
            // que va a responder Unauthorized no le sirve de nada.
            onClick={
              puedeVerBarrios
                ? () => navigate(`${ruta}/r-creditsbybarrio/t/t/t`)
                : undefined
            }
          />
        </CardGrid>
      </Box>

      <Box>
        <SectionLabel>Riesgo y colocación</SectionLabel>
        <CardGrid>
          <KpiCard
            icon={<PercentRoundedIcon fontSize="inherit" />}
            iconColor="error.main"
            iconBg="error.light"
            value={datos.morosidad === null ? SIN_DATO : `${datos.morosidad}%`}
            label="Tasa de morosidad"
            sub="Mora vencida sobre cartera viva"
            onClick={() => navigate(`${ruta}/credits`)}
          />

          <KpiCard
            icon={<EventBusyRoundedIcon fontSize="inherit" />}
            iconColor="warning.main"
            iconBg="warning.light"
            value={numero(datos.porVencer)}
            label="Créditos por vencer"
            sub="Vencen en los próximos 7 días"
            onClick={() => navigate(`${ruta}/credits`)}
          />

          <KpiCard
            icon={<TrendingUpRoundedIcon fontSize="inherit" />}
            iconColor="success.main"
            iconBg="success.light"
            value={cordobas(mesActual)}
            label="Colocación del mes"
            sub={`Mes anterior: ${cordobas(mesAnterior)}`}
            delta={deltaColocacion}
            deltaTone={tonoColocacion}
            onClick={() => navigate(`${ruta}/credits`)}
          />
        </CardGrid>
      </Box>

      <Box>
        <SectionLabel>Actividad</SectionLabel>
        <CardGrid>
          <KpiCard
            icon={<AttachMoneyRoundedIcon fontSize="inherit" />}
            iconColor="success.main"
            iconBg="success.light"
            value={cordobas(datos.cobrado)}
            label="Recuperación diaria"
            sub={enDolares(datos.cobrado)}
            onClick={() => navigate(`${ruta}/credits`)}
          />

          <KpiCard
            icon={<WarningAmberRoundedIcon fontSize="inherit" />}
            iconColor="error.main"
            iconBg="error.light"
            value={cordobas(datos.mora)}
            label="Mora total"
            sub={enDolares(datos.mora)}
            onClick={() => navigate(`${ruta}/credits`)}
          />

          <KpiCard
            icon={<ReceiptLongRoundedIcon fontSize="inherit" />}
            iconBg="warning.light"
            iconColor="warning.main"
            value={cordobas(datos.gastos)}
            label="Gastos del mes"
            sub={enDolares(datos.gastos)}
            onClick={() => navigate(`${ruta}/gastos`)}
          />

          <KpiMultiCard
            icon={<PersonAddAlt1RoundedIcon fontSize="inherit" />}
            iconColor="info.main"
            iconBg="info.light"
            label="Nuevos clientes"
            stats={[
              { label: "Hoy", value: datos.nuevosClientes.hoy },
              { label: "Semana", value: datos.nuevosClientes.semana },
              { label: "Mes", value: datos.nuevosClientes.mes },
            ]}
            onClick={() => navigate(`${ruta}/clients`)}
          />
        </CardGrid>
      </Box>
    </Box>
  );
};

export default HomeInfo;
